import axios from 'axios';

import { DEFAULT_PAGE_SIZE_GEONAMES_API } from '@/lib/constants/pagination';
import { ApiErrorCode, withApiErrorHandler } from '@/modules/common/api/errors';
import { DropdownSearchItem } from '@/modules/common/components/molecules/selectors/dropdown';
import { LocationFilterItemResponse } from '@/modules/common/types/dto/response/location-filter-item-response';
import { PaginatedResponse } from '@/modules/common/types/pagination-types';
import { axiosInstance } from '@/utils/axios';
import { logger } from '@/utils/logger/logger';

import { TeamLocationsResponse } from '../types/dto/response/team-locations-response';
import { GeoNamesCitySearchResponse, GeoNamesCountryInfoResponse } from '../types/geonames.types';

const TAG = 'GeonamesLocationAPI:';
const GEONAMES_USERNAME = import.meta.env.VITE_GEONAMES_API_USERNAME;
const GEONAMES_BASE_URL = 'https://secure.geonames.org';
const GEONAMES_PARAM_ORDER_BY_POPULATION = 'population';
const GEONAMES_PARAM_FEATURE_CLASS = 'P';
const GEONAMES_PARAM_STYLE = 'MEDIUM';

// Countries data is static — cache the result for the lifetime of the browser session.
// This avoids redundant network requests when country/city selectors are opened multiple times.
let countriesCache: DropdownSearchItem[] | null = null;
let countriesFetchPromise: Promise<DropdownSearchItem[]> | null = null;

/**
 * Gets countries data from GeoNames API.
 * Results are cached in memory for the browser session — the list of countries is static.
 */
export const getCountriesData = async (): Promise<DropdownSearchItem[]> => {
  if (countriesCache) return countriesCache;

  // Deduplicate concurrent calls — reuse the in-flight promise if one is already running
  if (countriesFetchPromise) return countriesFetchPromise;

  countriesFetchPromise = axios
    .get<GeoNamesCountryInfoResponse>(`${GEONAMES_BASE_URL}/countryInfoJSON`, {
      params: { username: GEONAMES_USERNAME },
    })
    .then(response => {
      const countries = response.data.geonames
        .map(country => ({
          id: country.countryCode,
          name: country.countryName,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      countriesCache = countries;
      return countries;
    })
    .catch(error => {
      logger.error(TAG, 'Failed to fetch countries from GeoNames', error);
      countriesFetchPromise = null; // Allow retry on next call
      throw error;
    })
    .finally(() => {
      countriesFetchPromise = null;
    });

  return countriesFetchPromise;
};

/**
 * Loads countries with pagination support for dropdown.
 * Returns all countries at once (no pagination needed - only ~250 countries).
 */
export const loadCountries = async (
  cursor?: number | null
): Promise<PaginatedResponse<DropdownSearchItem>> => {
  try {
    // Only fetch on first load (cursor is null)
    if (cursor !== null && cursor !== undefined) {
      return {
        items: [],
        pagination: {
          total: 0,
          cursor: null,
        },
      };
    }

    // Get all countries data at once
    const allCountries = await getCountriesData();

    return {
      items: allCountries,
      pagination: {
        total: allCountries.length,
        cursor: null, // No more pages
      },
    };
  } catch (error) {
    logger.error(TAG, 'Failed to load countries', error);
    throw error;
  }
};

/**
 * Searches countries with pagination support.
 * Returns all matching countries at once (no pagination needed).
 */
export const searchCountries = async (
  searchTerm: string,
  cursor?: number | null
): Promise<PaginatedResponse<DropdownSearchItem>> => {
  try {
    // Only fetch on first load (cursor is null)
    if (cursor !== null && cursor !== undefined) {
      return {
        items: [],
        pagination: {
          total: 0,
          cursor: null,
        },
      };
    }

    // Get all countries data
    const allCountries = await getCountriesData();

    // Filter by search term
    const filteredCountries = allCountries.filter(country =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return {
      items: filteredCountries,
      pagination: {
        total: filteredCountries.length,
        cursor: null, // No more pages
      },
    };
  } catch (error) {
    logger.error(TAG, 'Failed to search countries', error);
    throw error;
  }
};

/**
 * Loads cities for a specific country with pagination support.
 * Uses GeoNames API to fetch cities for the selected country.
 * Only includes populated places (cities, towns, villages).
 */
export const loadCities = async (
  countryCode: string,
  cursor?: number | null
): Promise<PaginatedResponse<DropdownSearchItem>> => {
  try {
    if (!countryCode) {
      return {
        items: [],
        pagination: {
          total: 0,
          cursor: null,
        },
      };
    }

    const startRow = cursor || 0;

    const response = await axios.get<GeoNamesCitySearchResponse>(
      `${GEONAMES_BASE_URL}/searchJSON`,
      {
        params: {
          username: GEONAMES_USERNAME,
          country: countryCode,
          featureClass: GEONAMES_PARAM_FEATURE_CLASS, // P = cities, towns, villages (populated places)
          maxRows: DEFAULT_PAGE_SIZE_GEONAMES_API,
          startRow,
          orderby: GEONAMES_PARAM_ORDER_BY_POPULATION, // Order by population for most relevant results
          style: GEONAMES_PARAM_STYLE,
        },
      }
    );

    const cities = response.data.geonames.map(city => ({
      id: city.geonameId.toString(),
      name: city.name,
      secondaryText: city.adminName1, // State/region name
    }));

    const total = response.data.totalResultsCount;
    const nextCursor =
      startRow + DEFAULT_PAGE_SIZE_GEONAMES_API < total
        ? startRow + DEFAULT_PAGE_SIZE_GEONAMES_API
        : null;

    return {
      items: cities,
      pagination: {
        total,
        cursor: nextCursor,
      },
    };
  } catch (error) {
    logger.error(TAG, `Failed to fetch cities for country ${countryCode}`, error);
    throw error;
  }
};

/**
 * Searches cities within a specific country.
 * Uses GeoNames API to search cities with the given term.
 */
export const searchCities = async (
  countryCode: string,
  searchTerm: string,
  cursor?: number | null
): Promise<PaginatedResponse<DropdownSearchItem>> => {
  try {
    if (!countryCode || !searchTerm) {
      return {
        items: [],
        pagination: {
          total: 0,
          cursor: null,
        },
      };
    }

    const startRow = cursor || 0;

    const response = await axios.get<GeoNamesCitySearchResponse>(
      `${GEONAMES_BASE_URL}/searchJSON`,
      {
        params: {
          username: GEONAMES_USERNAME,
          country: countryCode,
          featureClass: GEONAMES_PARAM_FEATURE_CLASS, // P = cities, towns, villages (populated places)
          name_startsWith: searchTerm, // Search by city name prefix
          maxRows: DEFAULT_PAGE_SIZE_GEONAMES_API,
          startRow,
          orderby: GEONAMES_PARAM_ORDER_BY_POPULATION, // Order by population for most relevant results
          style: GEONAMES_PARAM_STYLE,
        },
      }
    );

    const cities = response.data.geonames.map(city => ({
      id: city.geonameId.toString(),
      name: city.name,
      secondaryText: city.adminName1, // State/region name
    }));

    const total = response.data.totalResultsCount;
    const nextCursor =
      startRow + DEFAULT_PAGE_SIZE_GEONAMES_API < total
        ? startRow + DEFAULT_PAGE_SIZE_GEONAMES_API
        : null;

    return {
      items: cities,
      pagination: {
        total,
        cursor: nextCursor,
      },
    };
  } catch (error) {
    logger.error(TAG, `Failed to search cities in ${countryCode} with term "${searchTerm}"`, error);
    throw error;
  }
};

/**
 * Retrieves all unique team member locations from the organization.
 * Returns locations as a structured response grouped by country.
 * GET /api/team/locations
 *
 * @returns Array of location filter items with country and cities
 */
export const getTeamLocationsStructured = async (): Promise<LocationFilterItemResponse[]> => {
  const endpoint = '/api/team/locations';

  const response = await withApiErrorHandler(endpoint, ApiErrorCode.GENERIC_ERROR, () =>
    axiosInstance.get<{ data: TeamLocationsResponse }>(endpoint)
  );

  return response.locations || [];
};
